/**
 * TimelineDragHandlesLayer Component
 *
 * Renders interactive drag handles for phase editing.
 * Handles appear at phase boundaries (start, intersections, end).
 */

import React from 'react'
import { useTimelineStaticContext } from '../../context/timeline-static-context'
import { PhaseIntersectionHandle } from './phase-intersection-handle'
import {
  EXPANDED_ROW_HEIGHT,
  EXPANDED_ROW_HEIGHT_GAP,
  VERTICAL_DAY_TRACKER_SPACE,
} from '../../timeline-constants'

/**
 * TimelineDragHandlesLayer - Interactive drag handles
 *
 * Renders handles for:
 * - Start: Beginning of first phase
 * - Intersections: Between adjacent phases
 * - End: End of last phase
 *
 * @param {Object} props
 * @param {Array} props.crops - Transformed crop data
 * @param {boolean} props.forceAllExpand - Force all crops expanded
 * @param {number|string} props.draggingIndex - Currently dragging handle index
 * @param {number|string} props.hoveredIntersection - Currently hovered handle index
 * @param {Function} props.setHoveredIntersection - Hover handler
 * @param {Function} props.handleDragStart - Drag start handler
 */
export const TimelineDragHandlesLayer = React.memo(({
  crops,
  forceAllExpand,
  draggingIndex,
  hoveredIntersection,
  setHoveredIntersection,
  handleDragStart,
  barHeight,  // optional override for bar height (e.g. smaller succession rows)
}) => {
  const { expandedIds, weekWidth, getRowY, viewStrategy, collapsedRowHeight, collapsedRowHeightGap } = useTimelineStaticContext()

  return (
    <>
      {crops.map((crop) => {
        const isExpanded = forceAllExpand || expandedIds.includes(crop.id)
        const rowY = getRowY(crop.id) + VERTICAL_DAY_TRACKER_SPACE * 8

        // Calculate row height: double when showing both phases and tasks
        const baseRowH = isExpanded ? EXPANDED_ROW_HEIGHT : collapsedRowHeight
        const rowH = viewStrategy === 'both' && !crop.isOrgTaskRow ? baseRowH * 2 : baseRowH

        const barH = barHeight ?? (isExpanded
          ? EXPANDED_ROW_HEIGHT - EXPANDED_ROW_HEIGHT_GAP
          : collapsedRowHeight - collapsedRowHeightGap)

        // Position handles on phase bars (top half when both views shown)
        const subRowH = viewStrategy === 'both' && !crop.isOrgTaskRow ? rowH / 2 : rowH
        const y = viewStrategy === 'both' && !crop.isOrgTaskRow
          ? rowY + (subRowH / 2) - (barH / 2)
          : rowY + (rowH / 2) - (barH / 2)

        // Calculate first and last phase positions
        const firstPhase = crop.phases[0]
        const lastPhase = crop.phases[crop.phases.length - 1]
        const startX = firstPhase ? firstPhase.start * weekWidth : 0
        const endX = lastPhase ? (lastPhase.start + lastPhase.len) * weekWidth : 0

        return (
          <g key={`handles-${crop.id}`}>
            {/* Start handle (beginning of first phase) */}
            {firstPhase && (
              <PhaseIntersectionHandle
                x={startX}
                y={y}
                height={barH}
                intersectionIndex="start"
                onDragStart={handleDragStart}
                isDragging={draggingIndex === 'start'}
                isHovered={hoveredIntersection === 'start'}
                onHover={setHoveredIntersection}
              />
            )}

            {/* Intersection handles (between phases) */}
            {crop.phases.map((phase, pidx) => {
              const hasNextPhase = pidx < crop.phases.length - 1
              if (!hasNextPhase) return null

              const x = phase.start * weekWidth
              const phaseWidth = phase.len * weekWidth
              const intersectionX = x + phaseWidth

              return (
                <PhaseIntersectionHandle
                  key={`handle-${pidx}`}
                  x={intersectionX}
                  y={y}
                  height={barH}
                  intersectionIndex={pidx}
                  onDragStart={handleDragStart}
                  isDragging={draggingIndex === pidx}
                  isHovered={hoveredIntersection === pidx}
                  onHover={setHoveredIntersection}
                />
              )
            })}

            {/* End handle (end of last phase) */}
            {lastPhase && (
              <PhaseIntersectionHandle
                x={endX}
                y={y}
                height={barH}
                intersectionIndex="end"
                onDragStart={handleDragStart}
                isDragging={draggingIndex === 'end'}
                isHovered={hoveredIntersection === 'end'}
                onHover={setHoveredIntersection}
              />
            )}
          </g>
        )
      })}
    </>
  )
})
